/* ==========================================================================
   Product Collection Motion Pro
   File: assets/product-collection-motion-pro-card-media.js
   ========================================================================== */

(() => {
  'use strict';

  /* ==========================================================================
     01. GLOBAL CONSTANTS / HELPERS
     ========================================================================== */

  const CARD_SELECTOR = '[data-pcmp-card]';
  const IMAGE_SELECTOR = '[data-pcmp-media-image]';
  const CARD_VISIBLE_THRESHOLD = 0.15;

  const safeCall = (fn, ...args) => {
    try {
      if (typeof fn === 'function') {
        return fn(...args);
      }
    } catch (_) {}
    return undefined;
  };

  const parseNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const matchesFinePointer = () => {
    return !!(
      window.matchMedia &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
    );
  };


  /* ==========================================================================
     02. CARD MEDIA UNIT
     ========================================================================== */

  class PCMPCardMediaUnit {
    constructor(manager, card) {
      this.manager = manager;
      this.card = card;
      this.images = Array.from(card.querySelectorAll(IMAGE_SELECTOR));
      this.imageCount = this.images.length;
      this.intervalMs = Math.max(
        800,
        Number(manager?.settings?.imageAutoplayInterval || 1600)
      );

      this.activeIndex = this.getInitialActiveIndex();
      this.timer = null;

      this.isVisible = true;
      this.isHovered = false;
      this.isFocused = false;
      this.isMounted = false;

      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
    }

    getInitialActiveIndex() {
      const dataIndex = parseNumber(
        this.card.getAttribute('data-pcmp-card-media-active'),
        0
      );

      if (dataIndex >= 0 && dataIndex < this.imageCount) {
        return dataIndex;
      }

      const activeImageIndex = this.images.findIndex((image) =>
        image.classList.contains('is-active')
      );

      if (activeImageIndex >= 0) {
        return activeImageIndex;
      }

      return 0;
    }

    mount() {
      if (this.isMounted || this.imageCount <= 1) return;

      this.normalize();
      this.bind();
      this.syncPlayback();

      this.isMounted = true;
    }

    bind() {
      this.card.addEventListener('mouseenter', this.onMouseEnter);
      this.card.addEventListener('mouseleave', this.onMouseLeave);
      this.card.addEventListener('focusin', this.onFocusIn);
      this.card.addEventListener('focusout', this.onFocusOut);
    }

    unbind() {
      this.card.removeEventListener('mouseenter', this.onMouseEnter);
      this.card.removeEventListener('mouseleave', this.onMouseLeave);
      this.card.removeEventListener('focusin', this.onFocusIn);
      this.card.removeEventListener('focusout', this.onFocusOut);
    }

    normalize() {
      const safeIndex = this.getSafeIndex(this.activeIndex);
      this.setActive(safeIndex);
    }

    getSafeIndex(index) {
      if (this.imageCount <= 1) return 0;
      return ((index % this.imageCount) + this.imageCount) % this.imageCount;
    }

    setActive(index) {
      const safeIndex = this.getSafeIndex(index);
      this.activeIndex = safeIndex;

      this.images.forEach((image, imageIndex) => {
        const isActive = imageIndex === safeIndex;
        image.classList.toggle('is-active', isActive);
        image.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.card.setAttribute('data-pcmp-card-media-active', String(safeIndex));
    }

    next() {
      if (this.imageCount <= 1) return;
      this.setActive(this.activeIndex + 1);
    }

    start() {
      if (!this.canAutoplay()) return;

      this.stop();

      this.timer = window.setInterval(() => {
        if (!this.canAutoplay()) return;
        this.next();
      }, this.intervalMs);

      this.card.setAttribute('data-pcmp-card-media-running', 'true');
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }

      this.card.setAttribute('data-pcmp-card-media-running', 'false');
    }

    canAutoplay() {
      if (this.imageCount <= 1) return false;
      if (!this.manager.canAutoplay()) return false;
      if (!this.isVisible) return false;
      if (this.isFocused) return false;

      if (matchesFinePointer() && this.isHovered) {
        return false;
      }

      return true;
    }

    syncPlayback() {
      if (this.canAutoplay()) {
        this.start();
        return;
      }

      this.stop();
    }

    updateFromManager() {
      this.intervalMs = Math.max(
        800,
        Number(this.manager?.settings?.imageAutoplayInterval || 1600)
      );
      this.syncPlayback();
    }

    setVisible(isVisible) {
      this.isVisible = !!isVisible;
      this.syncPlayback();
    }

    onMouseEnter() {
      this.isHovered = true;
      this.syncPlayback();
    }

    onMouseLeave() {
      this.isHovered = false;
      this.syncPlayback();
    }

    onFocusIn() {
      this.isFocused = true;
      this.syncPlayback();
    }

    onFocusOut(event) {
      if (this.card.contains(event.relatedTarget)) return;

      this.isFocused = false;
      this.syncPlayback();
    }

    destroy() {
      this.stop();
      this.unbind();
      this.isMounted = false;
    }
  }


  /* ==========================================================================
     03. CARD MEDIA MANAGER
     ========================================================================== */

  class PCMPCardMediaManager {
    constructor(context) {
      this.context = context;
      this.root = context?.root || null;
      this.settings = context?.settings || {};
      this.cards = Array.from(context?.elements?.cards || []);

      this.units = [];
      this.cardObserver = null;

      this.isMounted = false;
      this.isDestroyed = false;
      this.sectionVisible = context?.state?.sectionVisible !== false;
      this.documentVisible = context?.state?.documentVisible !== false;
      this.editorSelected = false;
      this.prefersReducedMotion = !!context?.state?.prefersReducedMotion;
    }

    mount() {
      if (this.isMounted || this.isDestroyed) return;

      this.buildUnits();
      this.bindObservers();
      this.syncPlayback();

      this.isMounted = true;
    }

    refresh(context) {
      if (this.isDestroyed) return;

      const previousCards = this.cards;

      if (context) {
        this.context = context;
        this.root = context?.root || this.root;
        this.settings = context?.settings || this.settings;
        this.cards = Array.from(context?.elements?.cards || this.cards);
        this.prefersReducedMotion = !!context?.state?.prefersReducedMotion;
        this.sectionVisible = context?.state?.sectionVisible !== false;
        this.documentVisible = context?.state?.documentVisible !== false;
      }

      const cardsChanged =
        previousCards.length !== this.cards.length ||
        previousCards.some((card, index) => card !== this.cards[index]);

      if (cardsChanged) {
        this.rebuildUnits();
        return;
      }

      this.units.forEach((unit) => unit.updateFromManager());
      this.syncPlayback();
    }

    rebuildUnits() {
      this.disconnectObservers();
      this.destroyUnits();
      this.buildUnits();
      this.bindObservers();
      this.syncPlayback();
    }

    buildUnits() {
      const nextUnits = [];

      this.cards.forEach((card) => {
        if (!(card instanceof Element)) return;

        const images = Array.from(card.querySelectorAll(IMAGE_SELECTOR));
        if (images.length <= 1) return;

        const unit = new PCMPCardMediaUnit(this, card);
        unit.mount();
        nextUnits.push(unit);
      });

      this.units = nextUnits;
    }

    destroyUnits() {
      this.units.forEach((unit) => unit.destroy());
      this.units = [];
    }

    bindObservers() {
      if (!('IntersectionObserver' in window) || !this.units.length) return;

      this.cardObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const unit = this.units.find((item) => item.card === entry.target);
            if (!unit) return;

            const isVisible =
              entry.isIntersecting && entry.intersectionRatio >= CARD_VISIBLE_THRESHOLD;

            unit.setVisible(isVisible);
          });
        },
        {
          threshold: [0, CARD_VISIBLE_THRESHOLD, 0.5, 1],
        }
      );

      this.units.forEach((unit) => {
        this.cardObserver.observe(unit.card);
      });
    }

    disconnectObservers() {
      if (this.cardObserver) {
        this.cardObserver.disconnect();
        this.cardObserver = null;
      }
    }

    canAutoplay() {
      if (!this.settings.imageAutoplay) return false;
      if (!this.sectionVisible) return false;
      if (!this.documentVisible) return false;
      if (this.editorSelected) return false;

      if (this.settings.respectReducedMotion && this.prefersReducedMotion) {
        return false;
      }

      return true;
    }

    syncPlayback() {
      this.units.forEach((unit) => unit.syncPlayback());
    }

    setSectionVisible(isVisible) {
      this.sectionVisible = !!isVisible;
      this.syncPlayback();
    }

    setDocumentVisible(isVisible) {
      this.documentVisible = !!isVisible;
      this.syncPlayback();
    }

    onEditorSelect() {
      this.editorSelected = true;
      this.syncPlayback();
    }

    onEditorDeselect() {
      this.editorSelected = false;
      this.syncPlayback();
    }

    destroy() {
      if (this.isDestroyed) return;

      this.isDestroyed = true;
      this.isMounted = false;

      this.disconnectObservers();
      this.destroyUnits();
    }
  }


  /* ==========================================================================
     04. WINDOW EXPORT
     ========================================================================== */

  window.PCMPCardMediaManager = PCMPCardMediaManager;
})();